<template>
	<div class="inner-page cases">
				
		<AppHead title="Cases" />

		<PageHeader 
			title="Cases"
			:hasButton="permissions.canCreateProject"
			buttonLabel="Create new case"
			buttonRoute="/cases/new-case"
			:showHelper="true"
			helpTitle="Welcome to the Case List"
			helpDesc="Here is a list of all of the cases that you can view or edit. Please use the filters to refine which cases you would like to see."
			/>


		<div class="case-grid-container">
			<div></div>
			<div class="filter-result-data">
				Showing {{ cases.data.length }} of {{ cases.total }} cases
			</div>
		</div>



		<div class="case-grid-container">
			<div class="case-sidebar">

				<div class="search-bar" v-if="permissions.canSearchProjects">
					<input 
						type="text" 
						placeholder="Search for a case" 
						v-model="search"
						@keyup.enter="searchCases"
						/>
					<Button
						size="regular"
						color="black"
						icon="search"
						@click="searchCases"
						/>
				</div>

				<div 
					class="mobile-filter-bar"
					@click="toggleMobileFilter"
					>
					<div class="mobile-filter-title">Sorting &amp; Filters</div>
					<div class="right-side-filter-bar">
						<span 
							v-if="(selectedSortBy && selectedSortBy != 'Last modified') || selectedStatus || selectedStage || selectedTeam" 
							class="filter-count"
							>
							{{ [(selectedSortBy && selectedSortBy != 'Last modified'), selectedStatus, selectedStage, selectedTeam].filter(Boolean).length }}
						</span>
						<i class="fal fa-sliders-h"></i>
					</div>
				</div>

				<div 
					class="case-sidebar-inner"
					:class="{ 'open': mobileFiltersOpen }"
					>

					<div class="filter-group">
						<div class="filter-title">Sort by</div>
						<div class="filter-options">
							<ul>
								<li 
									v-for="(sort, index) in sortBy"
									:key="index"
									:class="{ 'selected': selectedSortBy === sort }"
									@click="sortCases(sort)"
									>
									<div class="selected-box" />
									{{ sort }}
								</li>
							</ul>
						</div>
					</div>

					<div 
						class="filter-group"
						v-if="['admin', 'global_viewer'].includes($page.props.user.user_type)"
						>
						<div class="filter-title">Filter by Distributor</div>
						<div class="filter-options">
							<ul>
								<li 
									:class="{ 'selected': !selectedTeam }"
									@click="filterTeam(false)"
									>
									<div class="selected-box" />
									All distributors
								</li>
								<li 
									v-for="(team, index) in allTeams"
									:key="index"
									:class="{ 'selected': selectedTeam == team.id }"
									@click="filterTeam(team.id)"
									>
									<div class="selected-box" />
									{{ team.name }}
								</li>
								<li 
									:class="{ 'selected': selectedTeam === 'null' }"
									@click="filterTeam('null')"
									>
									<div class="selected-box" />
									No team
								</li>
							</ul>
						</div>
					</div>

					<div class="filter-group">
						<div class="filter-title">Filter by Stage</div>
						<div class="filter-options">
							<ul>
								<li 
									:class="{ 'selected': !selectedStage }"
									@click="filterStage(false)"
									>
									<div class="selected-box" />
									All stages
								</li>
								<li 
									v-for="(stage, index) in stages"
									:key="index"
									:class="{ 'selected': selectedStage === index }"
									@click="filterStage(index)"
									>
									<div class="selected-box" />
									{{ stage }}
								</li>
							</ul>
						</div>
					</div>

					<div class="filter-group">
						<div class="filter-title">Filter by Status</div>
						<div class="filter-options">
							<ul>
								<li 
									:class="{ 'selected': !selectedStatus }"
									@click="filterStatus(false)"
									>
									<div class="selected-box" />
									All statuses
								</li>
								<li 
									v-for="(status, index) in statuses"
									:key="index"
									:class="{ 'selected': selectedStatus === status }"
									class="capitalize"
									@click="filterStatus(status)"
									>
									<div class="selected-box" />
									{{ status }}
								</li>
							</ul>
						</div>
					</div>

					<div 
						v-if="(selectedSortBy && selectedSortBy != 'Last modified') || selectedStatus || selectedStage || selectedTeam"
						class="filter-group"
						>
						<a href="/cases" class="clear-sort-filters">Clear sorting &amp; filters</a>
					</div>

				</div>
			</div>
		

			<div class="cases-list" v-if="cases && cases.data && cases.data.length > 0">
				<CaseOverviewRow 
					v-for="(theCase, index) in cases.data"
					:key="index"
					:thisCase="theCase"
					:caseID="theCase.hashid"
					:visibleCaseID="theCase.enquiry_number ? theCase.enquiry_number : theCase.hashid"
					:description="theCase.description"
					:steps="theCase.progress_stage"
					:commentCount="theCase.chat_count"
					:assignTeam="permissions.canAssignProjectToTeam"
					:assignManager="permissions.canAssignProjectManager"
					:team="theCase.teams"
					:teams="allTeams"
					:permissions="permissions"
					:allPotentialManagers="allPotentialManagers"
					/>
				
				
				

			</div>
			<Placeholder v-else message="No cases found" />

		</div>

		<div class="case-grid-container">
			<div class="mobile-order-2">

				<Link :href="`/cases/archive`">
					<Button 
						label="View Archive"
						link="/cases/archive"
						icon="archive"
						color="white"
						size="regular"
						/>
				</Link>

			</div>
			<div class="mobile-order-1">
				<!-- Pagination Controls -->
				<PaginationLinks :links="cases.links" />

			</div>
		</div>
		
		
			
			

	
	</div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout.vue"
import AppHead from "@/Components/AppHead"
import PageHeader from "@/Components/PageHeader"
import Subtitle from "@/Components/Subtitle"
import Placeholder from "@/Components/Placeholder"
import CaseOverviewRow from "@/Components/Cases/CaseOverviewRow"
import Button from "@/Components/Button"
import PaginationLinks from '@/Components/PaginationLinks'
import { Link } from '@inertiajs/inertia-vue3'

export default {
	components: {
		AppLayout,
		AppHead,
		PageHeader,
		Subtitle,
		Placeholder,
		CaseOverviewRow,
		Button,
		PaginationLinks,
		Link,
	},
	layout: AppLayout,
	props: [
		'cases',
		'allTeams',
		'permissions',
		'allPotentialManagers',
		'preselectedSortBy',
		'preselectedStatus',
		'preselectedStage',
		'preselectedTeam'
	],
	data: () => {
		return {
			search: '',
			stages: {
				0: 'Enquiry Submission',
				1: 'Image Submission',
				2: 'Design Meeting',
				3: 'Treatment Plan',
				4: 'Implant Production',
				5: 'Post Market Surveillance'
			},
			selectedStage: false,
			sortBy: [
				'Last modified',
				'Newest created',
				'Oldest created',
				'Order no. alphanumerically',
				'Rep A-Z',
				'Surgeon A-Z'
			],
			selectedSortBy: 'Last modified',
			selectedTeam: false,
			statuses: [
				'pending',
				'review',
				'accepted'
			],
			selectedStatus: false,
			mobileFiltersOpen: false
		}
	},
	methods: {
		searchCases(){
			if(this.search && this.search.length > 0){
				this.$inertia.get(`/cases/search/${encodeURIComponent(this.search)}`)
			}
		},
		sortCases(sort) {
			this.selectedSortBy = sort;
			this.updateCases();
		},
		filterStatus(status) {
			this.selectedStatus = status;
			this.updateCases();
		},
		filterStage(stage) {
			this.selectedStage = stage;
			this.updateCases();
		},
		filterTeam(team) {
			this.selectedTeam = team;
			this.updateCases();
		},
		updateCases() {
			let filters = {
				sort: this.selectedSortBy,
				status: this.selectedStatus,
				stage: this.selectedStage
			}

			if(['admin','global_viewer'].includes(this.$page.props.user.user_type)){
				filters.team = this.selectedTeam
			}

			this.$inertia.get('/cases', filters);
		},
		toggleMobileFilter(){
			this.mobileFiltersOpen = !this.mobileFiltersOpen
		}
	},
	mounted(){

		if(this.preselectedStatus && this.preselectedStatus != 'false'){
			this.selectedStatus = this.preselectedStatus
		}

		if(this.preselectedStage && this.preselectedStage != 'false'){
			this.selectedStage = this.preselectedStage
		}

		if(this.preselectedTeam && this.preselectedTeam != 'false'){
			this.selectedTeam = this.preselectedTeam
		}
		
		if(this.preselectedSortBy){
			this.selectedSortBy = this.preselectedSortBy
		}
	}
};
</script>

<style lang="sass">

.cases

	.case-grid-container
		width: 100%
		display: grid
		grid-template-columns: 260px 1fr
		grid-gap: 20px
		margin-bottom: 20px

		&:last-of-type
			margin-bottom: 70px

		@media (max-width: 1024px)
			display: flex
			flex-direction: column
			gap: 10px

			.mobile-order-1
				order: 1

			.mobile-order-2
				order: 2

	.case-sidebar
		width: 100%
		display: flex
		flex-direction: column
		border-radius: 8px
		gap: 10px

		.mobile-filter-bar
			display: none

			.right-side-filter-bar
				display: flex
				align-items: center
				gap: 10px

				.filter-count
					background-color: #333
					border-radius: 6px
					padding: 4px 8px
					font-size: 14px
					font-weight: 700
					color: #fff
					line-height: 1

			@media (max-width: 1024px)
				display: flex
				flex-direction: row
				justify-content: space-between
				align-items: center
				gap: 10px
				box-shadow: 0 1px 3px rgba(#000, 0.2)
				background-color: #fff
				border-radius: 8px
				padding: 10px 20px
				cursor: pointer

				.mobile-filter-title
					font-size: 15px
					font-weight: 700
					color: #252525

		.case-sidebar-inner
			width: 100%
			display: flex
			flex-direction: column
			box-shadow: 0 1px 3px rgba(#000, 0.2)
			background-color: #fff
			border-radius: 8px
			padding: 20px
			gap: 25px

			@media (max-width: 1024px)
				display: none

				&.open
					display: flex
				

		.filter-group
			width: 100%
			display: flex
			flex-direction: column
			gap: 10px

			.clear-sort-filters
				font-size: 14px
				font-weight: 700
				color: #252525
				text-decoration: underline
				cursor: pointer
				transition: all 0.2s

				&:hover
					color: #333

			.filter-title
				font-size: 16px
				font-weight: 700
				color: #252525

			.filter-options
				width: 100%
				display: flex
				flex-direction: column
				gap: 10px

				ul
					width: 100%
					display: flex
					flex-direction: column
					gap: 5px

					li
						font-size: 14px
						font-weight: 400
						color: #7c7c7c
						cursor: pointer
						transition: all 0.2s
						display: flex
						gap: 10px
						align-items: center

						&.captialize
							text-transform: capitalize

						.selected-box
							width: 16px
							height: 16px
							border: 2px solid #ccc
							border-radius: 4px
							transition: all 0.2s
						
						&:hover
							color: #252525

						&.selected
							color: #252525

							.selected-box
								background-color: #252525
								border-color: #252525
								transition: all 0.2s



	.cases-list
		width: 100%
		display: flex
		flex-direction: column
		gap: 10px

		&.archive
			opacity: 0.6

	.filter-result-data
		width: 100%
		font-size: 14px
		font-weight: 400
		color: #7c7c7c
		line-height: 1.1

	

	.search-bar
		width: 100%
		display: flex
		justify-content: flex-start
		align-items: center
		// gap: 20px

		input
			width: 100%
			max-width: 300px
			height: 42px
			border: 1px solid rgba(#000,0.1)
			border-radius: 6px 0 0 6px
			padding: 0 20px
			font-size: 16px
			font-weight: 400
			font-family: 'Roboto', sans-serif
			color: #252525

			@media (max-width: 1024px)
				max-width: 100%

			&::placeholder
				color: #777

		.button
			border-radius: 0 6px 6px 0

		

</style>